import { AnimatePresence, motion } from 'motion/react'
import { useCallback, useId } from 'react'
import { LoadingSpinner } from '@/components/loading-spinner'
import { ICityOption } from '@/features/cities/city-search'
import { SvgMapNeedle } from '@/icons/svg-map-needle'
import { IOnClickEvent } from '@/typings/onclick-event'
import { run } from '@/utils/run'
import { rgba } from '@/utils/styles/rgba'

interface ICityOptionProps {
    option: ICityOption
    onClick?: () => void | Promise<unknown>
    setIsLoading?: (isLoading: boolean) => void
    isLoading?: boolean
    highlighted?: boolean
}

export const CityOption = ({
    option,
    onClick: propsOnClick,
    isLoading,
    setIsLoading,
    highlighted,
}: ICityOptionProps) => {
    const componentId = useId()

    const onClick = useCallback(
        (e: IOnClickEvent) => {
            e.preventDefault()
            e.stopPropagation()

            const promise = propsOnClick?.()

            if (promise) {
                setIsLoading?.(true)
                promise.finally(() => setIsLoading?.(false))
            }
        },
        [propsOnClick, setIsLoading]
    )

    return (
        <motion.div
            animate={{ backgroundColor: highlighted ? rgba(0, 0, 0, 0.05) : 'white' }}
            whileHover={{
                backgroundColor: rgba(0, 0, 0, 0.05),
                transition: { filter: { duration: 0 } },
            }}
            transition={{
                backgroundColor: { duration: 0.1, ease: 'easeInOut' },
            }}
            onClick={onClick}
            className="flex items-center h-[73px] px-10 py-0 gap-x-4 bg-white webkit-tap-transparent relative w-full
                cursor-pointer rounded-m"
        >
            <div className="min-w-[20px] min-h-[20px] flex items-center justify-center">
                <SvgMapNeedle color="none" size={32} />
            </div>

            <p className="font-medium text-[18px]">{option.label}</p>

            <AnimatePresence>
                <motion.div
                    key={`address-display-option-right-container--${isLoading}--${componentId}`}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="flex items-center justify-center absolute right-4 transform-center-y"
                >
                    {run(() => {
                        if (isLoading) {
                            return <LoadingSpinner color="black" size={20} />
                        }

                        return null
                    })}
                </motion.div>
            </AnimatePresence>
        </motion.div>
    )
}
